/*
 * Header navigation enhancement (wireframe 13-navigation-v2).
 *
 * The Shop mega menu and the mobile drawer are native <details>, so click-toggle
 * (and body scroll-lock via the `scroll-lock` attribute — see critical.css) work
 * with no JS at all. This layer only adds what <details> can't do on its own:
 * desktop hover-intent, Escape, outside-click, and mirroring the open state onto
 * aria-expanded for the trigger.
 */
(() => {
  'use strict';

  const HOVER_OPEN_MS = 90;
  const HOVER_CLOSE_MS = 220;
  // Matches the CSS breakpoint where the drawer replaces the inline nav.
  const DESKTOP = '(min-width: 901px)';

  class SiteNav extends HTMLElement {
    connectedCallback() {
      this.mega = this.querySelector('[data-mega]');
      this.drawer = this.querySelector('[data-drawer]');
      this.openTimer = null;
      this.closeTimer = null;

      if (this.mega) {
        this.megaSummary = this.mega.querySelector('summary');
        this.syncExpanded();

        this.mega.addEventListener('toggle', this.syncExpanded);
        this.mega.addEventListener('pointerenter', this.onEnter);
        this.mega.addEventListener('pointerleave', this.onLeave);
        // A pointer press is an explicit choice — drop any pending hover timer
        // so the native <details> toggle isn't fought by it.
        this.mega.addEventListener('pointerdown', this.clearTimers);
      }

      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      this.clearTimers();
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
    }

    isDesktop = () => window.matchMedia(DESKTOP).matches;

    syncExpanded = () => {
      if (this.megaSummary) {
        this.megaSummary.setAttribute('aria-expanded', String(this.mega.open));
      }
    };

    clearTimers = () => {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
    };

    onEnter = () => {
      if (!this.isDesktop()) return;
      this.clearTimers();
      this.openTimer = window.setTimeout(() => {
        this.mega.open = true;
      }, HOVER_OPEN_MS);
    };

    onLeave = () => {
      if (!this.isDesktop()) return;
      this.clearTimers();
      this.closeTimer = window.setTimeout(() => {
        this.mega.open = false;
      }, HOVER_CLOSE_MS);
    };

    close = (details) => {
      if (details && details.open) details.open = false;
    };

    onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      this.clearTimers();
      // Return focus to the trigger so keyboard users aren't stranded.
      if (this.mega && this.mega.open && this.megaSummary) {
        this.close(this.mega);
        this.megaSummary.focus();
      }
      this.close(this.drawer);
    };

    onDocumentClick = (event) => {
      if (this.mega && this.mega.open && !this.mega.contains(event.target)) {
        this.clearTimers();
        this.close(this.mega);
      }
    };
  }

  if (!customElements.get('site-nav')) customElements.define('site-nav', SiteNav);
})();
